import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { theme } from './styles/theme';
import { GlobalStyles } from './styles/GlobalStyles';
import { BrandingProvider, useBranding } from './contexts/BrandingContext';
import { installAuthInterceptor } from './utils/authInterceptor';
import { enforceFreshBrowserSession } from './utils/session';
import SessionGuard from './components/SessionGuard';
import UserTasksRefresher from './components/UserTasksRefresher';
import ViewOnlyNotice from './components/ViewOnlyNotice';

// Components
import Login from './pages/Login/Login';
import VisitorEntry from './pages/VisitorEntry/VisitorEntry';
import ReturningClient from './pages/VisitorEntry/ReturningClient';
import StandbyPage from './pages/VisitorEntry/StandbyPage';
import SearchPage from './pages/VisitorEntry/SearchPage';
import IncompleteOrdersPage from './pages/VisitorEntry/IncompleteOrdersPage';
import TrackOrderPage from './pages/VisitorEntry/TrackOrderPage';
import PrescriptionForm from './pages/PrescriptionForm/PrescriptionForm';
import FrameSelection from './pages/FrameSelection/FrameSelection';
import Measurement from './pages/Measurement/Measurement';
import Payment from './pages/Payment/Payment';
import OrderConfirmation from './pages/OrderConfirmation/OrderConfirmation';
import OrderComplete from './pages/OrderComplete/OrderComplete';
import Inquiry from './pages/Inquiry/Inquiry';
import Quote from './pages/Quote/Quote';
import ProtectedRoute from './components/ProtectedRoute';
import { STORE_DASHBOARD_TASKS, LAB_TASKS, CENTRAL_TASKS } from './utils/tasks';
import SalesLayout from './components/layout/SalesLayout';
import LiveNotificationListener from './components/LiveNotificationListener';
import DashboardSwitcher from './components/DashboardSwitcher/DashboardSwitcher';

// Business Management Components
import SalesOverview from './pages/SalesOverview/SalesOverview';
import TechnicianDashboard from './pages/TechnicianDashboard/TechnicianDashboard';
import AdminLogin from './pages/AdminLogin/AdminLogin';
import AdminDashboard from './pages/AdminDashboard/AdminDashboard';
import ManagerDashboard from './pages/ManagerDashboard/ManagerDashboard';
import ClientsPage from './pages/Clients/ClientsPage';
import DoctorsPage from './pages/Doctors/DoctorsPage';
import OccupationsPage from './pages/Occupations/OccupationsPage';
import PeerRatingPage from './pages/PeerRating/PeerRatingPage';
import LabDashboard from './pages/LabDashboard/LabDashboard';
import AdminStaffHome from './pages/AdminStaff/AdminStaffHome';
import CentralStoreDashboard from './pages/CentralStoreDashboard/CentralStoreDashboard';
import CourierDashboard from './pages/CourierDashboard/CourierDashboard';
import CustomerSupportDashboard from './pages/CustomerSupportDashboard/CustomerSupportDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard/SuperAdminDashboard';
import PrintLabel from './pages/PrintLabel/PrintLabel';
import GlobalLanguageSwitcher from './components/LanguageSwitcher/GlobalLanguageSwitcher';
import { useTranslation } from 'react-i18next';

// Runs before anything renders: if the browser was closed and reopened, the
// stored session is discarded. At module scope on purpose — no route and no
// dashboard should get a chance to read a token that should already be gone.
enforceFreshBrowserSession();

// ─── Inner app — reads branding from context and injects into ThemeProvider ───
function AppWithTheme() {
  const { t } = useTranslation();
  installAuthInterceptor();
  const { branding } = useBranding();

  // Merge store branding colors into the base theme so styled-components
  // components that reference theme.colors.primary / accent reflect the
  // store's custom palette.  Falls back to base theme values when the
  // store hasn't set a custom color.
  const brandedTheme = useMemo(() => ({
    ...theme,
    colors: {
      ...theme.colors,
      primary:      branding.primaryColor || theme.colors.primary,
      primaryDark:  branding.primaryColor || theme.colors.primaryDark,
      primaryLight: branding.primaryColor || theme.colors.primaryLight,
      accent:       branding.accentColor  || theme.colors.accent,
      accentDark:   branding.accentColor  || theme.colors.accentDark,
      accentLight:  branding.accentColor  || theme.colors.accentLight,
    },
  }), [branding]);

  return (
    <ThemeProvider theme={brandedTheme}>
      <GlobalStyles />
      <Router>
        <div className="App">
          <SessionGuard />
          <UserTasksRefresher />
          <ViewOnlyNotice />
          <LiveNotificationListener />
          <GlobalLanguageSwitcher />
          <DashboardSwitcher />
          <Routes>
            {/* Dedicated QR label print page — public (data comes from the URL),
                opened in a popup and auto-prints. Browser-independent layout. */}
            <Route path="/print-label/:code" element={<PrintLabel />} />

            {/* Business Management Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/super-admin/dashboard"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/admin/dashboard" element={<Navigate to="/admin/dashboard/overview" replace />} />
            <Route
              path="/admin/dashboard/*"
              element={
                <ProtectedRoute requiredRole={['admin', 'advisor']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/manager/dashboard" element={<Navigate to="/manager/dashboard/overview" replace />} />
            <Route
              path="/manager/dashboard/*"
              element={
                <ProtectedRoute requiredTask={STORE_DASHBOARD_TASKS} requiredRole="store_manager">
                  <ManagerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/lab/dashboard"
              element={
                <ProtectedRoute requiredTask={LAB_TASKS} requiredRole={['lab_manager', 'technician']}>
                  <LabDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-staff/home"
              element={
                <ProtectedRoute requiredRole={[
                  'assistant',
                  'customer_support',
                  'accountant',
                  'courier',
                  'inventory_manager',
                  'operations_manager',
                  'other',
                ]}>
                  <AdminStaffHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/central-store/dashboard"
              element={
                <ProtectedRoute requiredTask={CENTRAL_TASKS} requiredRole={[
                  'inventory_manager',
                  'operations_manager',
                  'courier',
                ]}>
                  <CentralStoreDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/courier/dashboard"
              element={
                <ProtectedRoute requiredRole={['courier']}>
                  <CourierDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-support/dashboard"
              element={
                <ProtectedRoute requiredRole={['customer_support', 'admin']}>
                  <CustomerSupportDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute requiredRole={['sales_person', 'store_manager']}>
                  <SalesOverview />
                </ProtectedRoute>
              }
            />
            <Route path="/technician/dashboard" element={<TechnicianDashboard />} />

            <Route
              path="/clients"
              element={
                <ProtectedRoute requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.clientManagement')}>
                    <ClientsPage />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/doctors"
              element={
                <ProtectedRoute requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.doctors')}>
                    <DoctorsPage />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/occupations"
              element={
                <ProtectedRoute requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.occupations')}>
                    <OccupationsPage />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/peer-rating"
              element={
                <ProtectedRoute requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.peerRating')}>
                    <PeerRatingPage />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />

            {/* Visitor App Routes */}
            <Route path="/" element={<Login />} />
            <Route
              path="/visitor-entry"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <VisitorEntry />
                </ProtectedRoute>
              }
            />
            <Route
              path="/visitor-entry/returning"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.newVisit')}>
                    <ReturningClient />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visitor-entry/standby"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.standby')}>
                    <StandbyPage />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visitor-entry/search"
              element={
                <ProtectedRoute requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.unsuccessfulOrders')}>
                    <SearchPage />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visitor-entry/incomplete"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.unsuccessfulOrders')}>
                    <IncompleteOrdersPage />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visitor-entry/track"
              element={
                <ProtectedRoute requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.trackOrder')}>
                    <TrackOrderPage />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/prescription"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.prescriptionForm')}>
                    <PrescriptionForm />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/frame-selection"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.frameSelection')}>
                    <FrameSelection />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/measurement"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.measurement')}>
                    <Measurement />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.payment')}>
                    <Payment />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/order-confirmation"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.orderConfirmation')}>
                    <OrderConfirmation />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/order-complete"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.orderComplete')}>
                    <OrderComplete />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inquiry"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.inquiry')}>
                    <Inquiry />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quote"
              element={
                <ProtectedRoute requiredTask="sales.create" requiredRole={['sales_person', 'store_manager']}>
                  <SalesLayout pageTitle={t('pageTitles.quote')}>
                    <Quote />
                  </SalesLayout>
                </ProtectedRoute>
              }
            />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <BrandingProvider>
      <AppWithTheme />
    </BrandingProvider>
  );
}

export default App;
